import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  VpnKey,
  Warning,
} from '@mui/icons-material';
import { useTenant } from '../hooks/useTenant';
import { useSecretsService, SecretKey, SecretCreate } from '../services/secretsService';
import { useAuth0 } from '@auth0/auth0-react';

const Secrets: React.FC = () => {
  const { currentTenant, isTenantSelected } = useTenant();
  const { getAccessTokenSilently } = useAuth0();
  const secretsService = useSecretsService();
  const [secrets, setSecrets] = useState<SecretKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSecret, setEditingSecret] = useState<SecretKey | null>(null);
  const [secretKey, setSecretKey] = useState('');
  const [secretValue, setSecretValue] = useState('');

  const loadSecrets = useCallback(async () => {
    if (!currentTenant) return;

    setLoading(true);
    setError(null);

    try {
      const response = await secretsService.getTenantSecrets(currentTenant.name);
      setSecrets(response.secrets);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar secrets');
      setSecrets([]); // Limpar a tabela quando houver erro
      console.error('Erro ao carregar secrets:', err);
    } finally {
      setLoading(false);
    }
  }, [currentTenant]);

  // Forçar renovação do token quando o tenant muda
  useEffect(() => {
    if (isTenantSelected && currentTenant) {
      const refreshTokenForNewTenant = async () => {
        try {
          await getAccessTokenSilently({ cacheMode: 'off' });
        } catch (error) {
          console.warn('Erro ao renovar token para novo tenant:', error);
        }
      };

      refreshTokenForNewTenant();
    }
  }, [currentTenant, isTenantSelected, getAccessTokenSilently]);

  // Carregar secrets da API
  useEffect(() => {
    if (isTenantSelected && currentTenant) {
      loadSecrets();
    }
  }, [currentTenant?.name, isTenantSelected]); // Usar apenas currentTenant.name para evitar loop

  const handleAddSecret = () => {
    setEditingSecret(null);
    setSecretKey('');
    setSecretValue('');
    setOpenDialog(true);
  };

  const handleEditSecret = (secret: SecretKey) => {
    setEditingSecret(secret);
    setSecretKey(secret.key);
    setSecretValue('');
    setOpenDialog(true);
  };

  const handleDeleteSecret = async (secretKey: string) => {
    if (!currentTenant || !confirm(`Tem certeza que deseja deletar o secret "${secretKey}"?`)) {
      return;
    }

    try {
      await secretsService.deleteSecret(currentTenant.name, secretKey);
      setSecrets(prev => prev.filter(s => s.key !== secretKey));
      setSuccessMessage('Secret deletado com sucesso');
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro ao deletar secret');
      }
      console.error('Erro ao deletar secret:', err);
    }
  };

  const handleSaveSecret = async () => {
    if (!currentTenant || !secretKey.trim()) {
      setError('Nome do secret é obrigatório');
      return;
    }

    try {
      const secretData: SecretCreate = {
        key: secretKey,
        value: secretValue,
      };

      await secretsService.createOrUpdateSecret(currentTenant.name, secretData);

      // Recarregar secrets para obter dados atualizados
      await loadSecrets();

      setOpenDialog(false);
      setError(null);
      setSuccessMessage(editingSecret ? 'Secret atualizado com sucesso' : 'Secret criado com sucesso');
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro ao salvar secret');
      }
      console.error('Erro ao salvar secret:', err);
    }
  };



  if (!isTenantSelected) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" icon={<Warning />}>
          Selecione um tenant para gerenciar os secrets
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, ml: 12.5 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKey />
          Secrets
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gerenciando secrets do tenant: <strong>{currentTenant?.name}</strong>
        </Typography>
      </Box>

      {/* Actions */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddSecret}
          disabled={!!error} // Desabilitar quando houver erro
          sx={{ backgroundColor: currentTenant?.color }}
        >
          Adicionar Secret
        </Button>

        <Chip
          label={`${secrets.length} secrets`}
          color="primary"
          variant="outlined"
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Secrets Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome do Secret</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : secrets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography color="text.secondary">
                      Nenhum secret encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                secrets.map((secret) => (
                  <TableRow key={secret.key}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {secret.key}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          disabled={!!error} // Desabilitar quando houver erro
                          onClick={() => handleEditSecret(secret)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Deletar">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={!!error} // Desabilitar quando houver erro
                          onClick={() => handleDeleteSecret(secret.key)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSecret ? 'Editar Secret' : 'Adicionar Secret'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do Secret"
            fullWidth
            variant="outlined"
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="Ex: API_TOKEN"
            helperText="Use letras maiúsculas e underscores"
          />
          <TextField
            margin="dense"
            label="Valor do Secret"
            fullWidth
            variant="outlined"
            type="password"
            value={secretValue}
            onChange={(e) => setSecretValue(e.target.value)}
            placeholder="Digite o valor do secret"
            helperText="O valor será criptografado e não será exibido novamente"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleSaveSecret}
            variant="contained"
            disabled={!!error} // Desabilitar quando houver erro
            sx={{ backgroundColor: currentTenant?.color }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Secrets;
